import type { PriceId, QualifyingStatusEnum } from '@iheartjane/types';
import compact from 'lodash-es/compact';
import find from 'lodash-es/find';
import sumBy from 'lodash-es/sumBy';
import uniq from 'lodash-es/uniq';

import { EXTRACT } from '../constants';
import type {
  QualifyingMetricsSpecial,
  QualifyingStatusData,
  QualifyingStatusProduct,
  RulesAndConditionsSpecial,
} from './storeSpecialDetailUtils.types';
import {
  bulkWeightsForRules,
  priceIdsForWeightRules,
  shouldReadRuleSets,
} from './weightRules';

// parse whether a special allows discounts on required products (aka mix-and-match)
export const allowDiscountsOnRequiredProducts = (
  special?: RulesAndConditionsSpecial
) => {
  if (!special) return false;

  const { special_type } = special;

  if (special_type === 'bundle' || special_type === 'spending_threshold') {
    return (
      special.rules?.settings?.allow_discounts_on_required_products ?? false
    );
  }

  return false;
};

export const getDiscountedWithoutSameQualified = (qualifyingStatusData?: {
  discounted_products: QualifyingStatusProduct[];
  qualified_products: QualifyingStatusProduct[];
  qualifying_status: QualifyingStatusEnum;
}) => {
  return qualifyingStatusData?.discounted_products
    ? qualifyingStatusData.discounted_products
        .filter(
          (product) =>
            !find(qualifyingStatusData.qualified_products, {
              id: product.id,
              price_id: product.price_id,
              count: product.count,
            })
        )
        .map((product) => {
          const qualifiedProductCount = find(
            qualifyingStatusData.qualified_products,
            {
              id: product.id,
              price_id: product.price_id,
            }
          )?.count;

          return qualifiedProductCount
            ? { ...product, count: product.count - qualifiedProductCount }
            : product;
        })
    : [];
};

interface GetPriceIdsForStoreSpecialArgs {
  expandEachWeight?: boolean;
  multipleConditions?: boolean;
  qualifyingStatus?: QualifyingStatusEnum;
  special?: RulesAndConditionsSpecial;
}

// price_ids that should be rendered on product cards
export const getPriceIdsForStoreSpecial = ({
  expandEachWeight = false,
  multipleConditions,
  qualifyingStatus,
  special,
}: GetPriceIdsForStoreSpecialArgs): PriceId[] => {
  if (!special) return [];

  const { special_type } = special;

  if (special_type === 'bundle') {
    const isQualified = ['qualified', 'applied'].includes(
      String(qualifyingStatus)
    );

    // Bundle rules carry qualifier/discountable as arrays of sections
    const section = isQualified
      ? special.rules?.discountable?.[0]
      : special.rules?.qualifier?.[0];

    if (shouldReadRuleSets({ multipleConditions, section })) {
      return priceIdsForWeightRules({
        expandEachWeight,
        multipleConditions,
        section,
      });
    }

    const weights = section?.includes?.[0]?.weights;

    // Check if weights contains 'each' and return full list if feature flag is enabled
    if (expandEachWeight && weights?.includes('each')) {
      return EXTRACT.map(({ id }) => id);
    }

    return weights ?? [];
  }

  if (special_type === 'spending_threshold') {
    const isQualified = ['qualified', 'applied'].includes(
      String(qualifyingStatus)
    );

    return priceIdsForWeightRules({
      expandEachWeight,
      multipleConditions,
      section: isQualified
        ? special.rules?.discountable
        : special.rules?.qualifier,
    });
  }

  if (special_type === 'bulk_pricing') {
    const bulkWeights = bulkWeightsForRules({
      multipleConditions,
      rules: special.rules,
    });

    // Check if any bulk weight price_id is 'each' and return full list if feature flag is enabled
    if (
      expandEachWeight &&
      bulkWeights.some(({ price_id }) => price_id === 'each')
    ) {
      return EXTRACT.map(({ id }) => id);
    }

    return uniq(compact(bulkWeights.map(({ price_id }) => price_id)));
  }

  if (special_type === 'product' || special_type === 'cart_total') {
    return priceIdsForWeightRules({
      expandEachWeight,
      multipleConditions,
      section: special.rules,
    });
  }

  return [];
};

// calculate remainingQualifiers, remainingDiscountables, and reachedMaxDiscountables for a special based on its type and qualifying status
export const getSpecialQualifyingMetrics = ({
  currentSpecial,
  qualifyingStatus,
}: {
  currentSpecial: QualifyingMetricsSpecial;
  qualifyingStatus?: QualifyingStatusData;
}) => {
  const { special_type } = currentSpecial;

  let remainingQualifiers = 0;
  if (special_type === 'bulk_pricing') {
    remainingQualifiers =
      (qualifyingStatus?.target_weight ?? 0) -
      (qualifyingStatus?.product_weight ?? 0);
  } else if (
    special_type === 'spending_threshold' ||
    special_type === 'cart_total'
  ) {
    remainingQualifiers =
      (currentSpecial.rules?.settings?.cart_threshold ?? 0) -
      (qualifyingStatus?.product_price ?? 0);
  } else if (special_type === 'bundle') {
    remainingQualifiers =
      (currentSpecial.rules?.settings?.threshold_number_of_items_in_cart ?? 0) -
      sumBy(qualifyingStatus?.qualified_products, 'count');
  }

  let maxDiscountedProducts = 0;
  if (special_type === 'spending_threshold') {
    maxDiscountedProducts =
      currentSpecial.rules?.settings?.max_number_of_discounted_products ?? 0;
  } else if (special_type === 'bundle') {
    maxDiscountedProducts =
      currentSpecial.rules?.settings?.max_number_of_discounted_products ?? 0;
  }

  const remainingDiscountables =
    maxDiscountedProducts -
    sumBy(qualifyingStatus?.discounted_products, 'count');

  const reachedMaxDiscountables =
    ((special_type === 'spending_threshold' || special_type === 'bundle') &&
      remainingDiscountables === 0) ||
    (special_type === 'bulk_pricing' && remainingQualifiers === 0);

  return {
    reachedMaxDiscountables,
    remainingDiscountables,
    remainingQualifiers,
  };
};
